import { useState } from 'react';

import {
  Box,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

interface LogResultTableProps {
  result: string;
}

export const LogResultTable = ({ result }: LogResultTableProps) => {
  const [viewMode, setViewMode] = useState<'Table' | 'JSON'>('Table');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  const rows = result.split('\n').filter((r) => !r.includes('<truncated>'));
  const totalRecords = rows.length;
  const showPagination = totalRecords > 10;

  const headers: string[] = [];
  rows.forEach((r) => {
    try {
      const row = JSON.parse(r);
      Object.keys(row).forEach((k) => {
        if (!headers.includes(k)) {
          headers.push(k);
        }
      });
    } catch (e) {
      // Skip invalid JSON rows
    }
  });

  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRows = rows.slice(startIndex, endIndex);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleToggle = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'Table' | 'JSON' | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (headers.length === 0) {
    return <span>{result}</span>;
  }

  return (
    <Box className="logs-printer-wrap">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {showPagination && viewMode === 'Table' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', gap: 0.5 }}>
            <Pagination
              count={Math.ceil(totalRecords / recordsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              size="small"
              siblingCount={2}
              boundaryCount={1}
            />
            <Typography variant="caption" sx={{ fontStyle: 'oblique', fontSize: '11px' }}>
              Pages of {recordsPerPage} with a maximum of 100 records
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            width: showPagination && viewMode === 'Table' ? '50%' : '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleToggle}
            size="small"
            sx={{ height: 32 }}
          >
            <ToggleButton value="Table" sx={{ px: 2, fontSize: '12px' }}>
              Table
            </ToggleButton>
            <ToggleButton value="JSON" sx={{ px: 2, fontSize: '12px' }}>
              JSON
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === 'JSON' ? (
        <Box sx={{ whiteSpace: 'pre-wrap', fontSize: '13px', fontFamily: 'monospace' }}>
          {result}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ border: '1px solid #ddd' }}>
            <TableHead>
              <TableRow>
                {headers.map((h, i) => (
                  <TableCell
                    key={i}
                    sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((rowStr, i) => {
                let row: Record<string, any> = {};
                try {
                  row = JSON.parse(rowStr);
                } catch (e) {
                  return null;
                }

                if (Object.keys(row).length === 0) return null;

                return (
                  <TableRow
                    key={i}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {headers.map((k, ii) => {
                      const val = row[k];
                      return (
                        <TableCell key={ii} sx={{ whiteSpace: 'nowrap' }}>
                          <pre
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              margin: 0,
                              fontSize: '13px',
                            }}
                          >
                            {JSON.stringify(val, null, 2)}
                          </pre>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
